import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import { GoalConfirmModal, GoalEditModal } from '../GoalsCard/GoalModals';
import V2GoalRow from '../GoalsCard/V2GoalRow';
import {
    emptyGoalGroups,
    getCachedGoalGroups,
    invalidateGoalsCache,
    normalizeGoalGroups,
    setCachedGoalGroups,
} from '../goalsCache';
import './GoalsWorkspace.css';

const TABS = [
    { id: 'inProgress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' },
    { id: 'pending', label: 'Additional' },
];

function formatDueDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `Target ${mm}/${dd}/${yyyy}`;
}

function accentFromHex(hex) {
    if (!hex || typeof hex !== 'string') return '#c9a227';
    const raw = hex.replace('#', '').trim();
    const full =
        raw.length === 3
            ? raw
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : raw;
    if (full.length !== 6) return '#c9a227';
    return `#${full}`;
}

function goalRowKey(goal, index, tab) {
    const idPart =
        goal?.id != null && goal.id !== '' ? String(goal.id) : 'noid';
    return `${tab}-${index}-${idPart}`;
}

/**
 * Full Goals workspace for Dashboard V2 — all statuses, same APIs as classic My Goals.
 * Optional `embedded` mode (no outer back / glass chrome) for nesting in other V2 panels.
 */
const GoalsWorkspace = ({
    onBack,
    onProgressRefresh,
    onGoalCompleted,
    active = true,
    embedded = false,
}) => {
    const { checkUserSession } = useContext(AppContext);
    const { token, userId } = checkUserSession();
    const cached = userId ? getCachedGoalGroups(userId) : null;
    const [tab, setTab] = useState('inProgress');
    const [groups, setGroups] = useState(() => cached || emptyGoalGroups());
    const [loading, setLoading] = useState(() => !cached);
    const [errorMsg, setErrorMsg] = useState('');
    const [menuGoalId, setMenuGoalId] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [editGoal, setEditGoal] = useState(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const menuRef = useRef(null);
    const wasInactiveRef = useRef(false);

    const applyGroups = useCallback(
        (next) => {
            setGroups(next);
            if (userId) setCachedGoalGroups(userId, next);
        },
        [userId]
    );

    const loadGoals = useCallback(
        async ({ soft = false } = {}) => {
            if (!token || !userId) return;
            const hit = getCachedGoalGroups(userId);
            if (hit) {
                setGroups(hit);
                setLoading(false);
            } else if (!soft) {
                setLoading(true);
            }
            if (!soft) setErrorMsg('');

            const { error, data } = await requestAxios({
                url: `goals/GetUserGoalsGroupedByStatus/${userId}`,
                token,
            });
            if (!error && data) {
                applyGroups(normalizeGoalGroups(data));
                setErrorMsg('');
            } else if (!hit) {
                setErrorMsg('Could not load goals.');
            }
            setLoading(false);
        },
        [token, userId, applyGroups]
    );

    useEffect(() => {
        loadGoals({ soft: Boolean(getCachedGoalGroups(userId)) });
    }, [loadGoals, userId]);

    useEffect(() => {
        if (!active) {
            wasInactiveRef.current = true;
            return;
        }
        if (!wasInactiveRef.current) return;
        wasInactiveRef.current = false;
        loadGoals({ soft: true });
    }, [active, loadGoals]);

    useEffect(() => {
        if (menuGoalId == null) return undefined;
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuGoalId(null);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [menuGoalId]);

    const list = useMemo(() => groups[tab] || [], [groups, tab]);

    const counts = useMemo(
        () => ({
            inProgress: groups.inProgress.length,
            completed: groups.completed.length,
            pending: groups.pending.length,
        }),
        [groups]
    );

    const closeMenus = () => {
        setMenuGoalId(null);
        setConfirm(null);
        setEditGoal(null);
        setActionError('');
    };

    const handleComplete = async () => {
        if (!confirm?.goal || busy) return;
        const goalId = confirm.goal.id;
        const snapshot = groups;
        const nextCount = groups.completed.length + 1;
        setBusy(true);
        setActionError('');
        setConfirm(null);
        applyGroups({
            ...groups,
            inProgress: groups.inProgress.filter((g) => g.id !== goalId),
            pending: groups.pending.filter((g) => g.id !== goalId),
            completed: [
                confirm.goal,
                ...groups.completed.filter((g) => g.id !== goalId),
            ],
        });
        if (typeof onGoalCompleted === 'function') onGoalCompleted(nextCount);

        const { error } = await requestAxios({
            url: 'Goals/CompleteDashboardGoal',
            method: 'put',
            token,
            data: { userId, id: goalId },
        });
        setBusy(false);
        if (error) {
            applyGroups(snapshot);
            setActionError('Could not complete goal. Try again.');
            if (typeof onGoalCompleted === 'function') onGoalCompleted(null);
            return;
        }
        invalidateGoalsCache(userId);
        if (typeof onProgressRefresh === 'function') onProgressRefresh();
        loadGoals({ soft: true });
    };

    const handleRemove = async () => {
        if (!confirm?.goal || busy) return;
        const goalId = confirm.goal.id;
        const snapshot = groups;
        setBusy(true);
        setActionError('');
        setConfirm(null);
        applyGroups({
            inProgress: groups.inProgress.filter((g) => g.id !== goalId),
            completed: groups.completed.filter((g) => g.id !== goalId),
            pending: groups.pending.filter((g) => g.id !== goalId),
        });

        const { error } = await requestAxios({
            url: `Goals/${goalId}`,
            method: 'delete',
            token,
        });
        setBusy(false);
        if (error) {
            applyGroups(snapshot);
            setActionError('Could not remove goal. Try again.');
            return;
        }
        invalidateGoalsCache(userId);
        await loadGoals({ soft: true });
        if (typeof onProgressRefresh === 'function') onProgressRefresh();
    };

    const handleEditSave = async ({ id, text, completedByDate }) => {
        if (busy) return;
        const snapshot = groups;
        setBusy(true);
        setActionError('');
        setEditGoal(null);
        const patchList = (arr) =>
            arr.map((g) =>
                g.id === id ? { ...g, text, completedByDate } : g
            );
        applyGroups({
            inProgress: patchList(groups.inProgress),
            completed: patchList(groups.completed),
            pending: patchList(groups.pending),
        });

        const { error } = await requestAxios({
            url: 'Goals/UpdateDashboardGoal',
            method: 'put',
            token,
            data: { userId, id, text, completedByDate },
        });
        setBusy(false);
        if (error) {
            applyGroups(snapshot);
            setActionError('Could not save goal. Try again.');
            return;
        }
        await loadGoals({ soft: true });
    };

    const handleAddToTop7 = async () => {
        if (!confirm?.goal || busy) return;
        if (groups.inProgress.length >= 7) {
            setActionError(
                'You already have 7 goals in progress. It is not possible to add more.'
            );
            return;
        }
        const goal = confirm.goal;
        const goalId = goal.id;
        if (goalId == null || goalId === '') {
            setConfirm(null);
            setActionError(
                'Could not add goal to My Top 7. Refresh and try again.'
            );
            return;
        }
        const snapshot = groups;
        setBusy(true);
        setActionError('');
        setConfirm(null);
        applyGroups({
            ...groups,
            pending: groups.pending.filter((g) => g.id !== goalId),
            inProgress: [...groups.inProgress, goal],
        });

        const { error } = await requestAxios({
            url: 'Goals/SetGoalInProgress',
            method: 'put',
            token,
            data: { userId, id: goalId },
        });
        setBusy(false);
        if (error) {
            applyGroups(snapshot);
            setActionError('Could not add goal to My Top 7. Try again.');
            return;
        }
        invalidateGoalsCache(userId);
        await loadGoals({ soft: true });
        if (typeof onProgressRefresh === 'function') onProgressRefresh();
    };

    const confirmCopy =
        confirm?.type === 'complete'
            ? 'Did you complete this goal?'
            : confirm?.type === 'addToTop7'
              ? 'Do you want to add this goal to your My Top 7?'
              : 'Do you really want to delete this goal?';

    const emptyCopy =
        tab === 'completed'
            ? 'No completed goals yet.'
            : tab === 'pending'
              ? 'No additional goals yet.'
              : 'No goals in progress. Add them from Design My Life when you are ready.';

    const hasCachedData =
        counts.inProgress + counts.completed + counts.pending > 0;
    const showLoading = loading && !hasCachedData;

    return (
        <section
            className={`dv2-gw dv2-glass${
                embedded ? ' dv2-gw--embedded' : ' dv2-gw--card'
            }`}
            hidden={!active}
            aria-hidden={!active}
        >
            {!embedded ? (
                <div className="dv2-gw__top">
                    <button
                        type="button"
                        className="dv2-gw__back"
                        onClick={onBack}
                    >
                        ← Back to dashboard
                    </button>
                </div>
            ) : null}

            <header className="dv2-gw__header">
                <div className="dv2-gw__header-left">
                    <div className="dv2-gw__badge" aria-hidden>
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                        >
                            <path d="M5 21V4" />
                            <path d="M5 4h11l-2.5 4L16 12H5" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="dv2-gw__title">My Goals</h1>
                        <p className="dv2-gw__subtitle">
                            Your Top 7 and progress across every life area
                        </p>
                    </div>
                </div>
                <div className="dv2-gw__stat-pill">
                    <strong>{counts.inProgress}</strong>
                    <span>IN PROGRESS</span>
                </div>
            </header>

            <nav className="dv2-gw__tabs" aria-label="Goal status">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`dv2-gw__tab${
                            tab === t.id ? ' is-active' : ''
                        }`}
                        onClick={() => {
                            setTab(t.id);
                            setMenuGoalId(null);
                        }}
                    >
                        {t.label}
                        <em>{counts[t.id]}</em>
                    </button>
                ))}
            </nav>

            <div className="dv2-gw__body">
                <div className="dv2-gw__body-head">
                    <h2 className="dv2-gw__body-title">
                        {tab === 'inProgress'
                            ? 'In Progress'
                            : tab === 'completed'
                              ? 'Completed'
                              : 'Additional Goals'}
                    </h2>
                    <span className="dv2-gw__body-meta">
                        {list.length}{' '}
                        {list.length === 1 ? 'goal' : 'goals'}
                    </span>
                </div>

                {showLoading ? (
                    <p className="dv2-gw__status">Loading goals…</p>
                ) : null}
                {errorMsg ? <p className="dv2-gw__error">{errorMsg}</p> : null}
                {actionError && !confirm && !editGoal ? (
                    <p className="dv2-gw__error">{actionError}</p>
                ) : null}

                {!showLoading && !errorMsg && list.length === 0 ? (
                    <p className="dv2-gw__status">{emptyCopy}</p>
                ) : null}

                {!showLoading && list.length > 0 ? (
                    <ul
                        className={`dv2-gw__list${
                            menuGoalId != null ? ' is-menu-open' : ''
                        }`}
                    >
                        {list.map((goal, index) => {
                            const detail = formatDueDate(goal.completedByDate);
                            const rowKey = goalRowKey(goal, index, tab);
                            const canAct =
                                tab === 'inProgress' || tab === 'pending';
                            const canAddToTop7 =
                                tab === 'pending' && counts.inProgress < 7;
                            const menuOpen = menuGoalId === rowKey;
                            const isOolaOne =
                                tab === 'inProgress' &&
                                (Boolean(goal.isOneGoal) ||
                                    index === 0 ||
                                    Number(goal.goalOrder) === 1);
                            const statusLabel =
                                tab === 'inProgress'
                                    ? 'In Progress'
                                    : tab === 'completed'
                                      ? 'Completed'
                                      : 'Pending';
                            return (
                                <V2GoalRow
                                    key={rowKey}
                                    goalNumber={
                                        tab === 'inProgress'
                                            ? goal.goalOrder || index + 1
                                            : index + 1
                                    }
                                    title={goal.text}
                                    category={goal.areaName}
                                    targetDate={detail}
                                    status={statusLabel}
                                    isCompleted={tab === 'completed'}
                                    isOolaOne={isOolaOne}
                                    accentColor={accentFromHex(goal.color)}
                                    menuOpen={menuOpen}
                                    menuOpensUp={index >= list.length - 2}
                                    menuRef={menuRef}
                                    showMenu={canAct}
                                    showAddToTop7={canAddToTop7}
                                    rowClickable={canAct && !busy}
                                    onMenuToggle={() =>
                                        setMenuGoalId(
                                            menuOpen ? null : rowKey
                                        )
                                    }
                                    onAddToTop7={() => {
                                        setMenuGoalId(null);
                                        setActionError('');
                                        if (counts.inProgress >= 7) {
                                            setActionError(
                                                'You already have 7 goals in progress. It is not possible to add more.'
                                            );
                                            return;
                                        }
                                        setConfirm({
                                            type: 'addToTop7',
                                            goal,
                                        });
                                    }}
                                    onComplete={() => {
                                        setMenuGoalId(null);
                                        setActionError('');
                                        setConfirm({
                                            type: 'complete',
                                            goal,
                                        });
                                    }}
                                    onEdit={() => {
                                        setMenuGoalId(null);
                                        setActionError('');
                                        setEditGoal(goal);
                                    }}
                                    onRemove={() => {
                                        setMenuGoalId(null);
                                        setActionError('');
                                        setConfirm({
                                            type: 'remove',
                                            goal,
                                        });
                                    }}
                                />
                            );
                        })}
                    </ul>
                ) : null}
            </div>

            <GoalConfirmModal
                open={Boolean(confirm)}
                message={confirmCopy}
                busy={busy}
                error={actionError}
                onCancel={closeMenus}
                onConfirm={
                    confirm?.type === 'complete'
                        ? handleComplete
                        : confirm?.type === 'addToTop7'
                          ? handleAddToTop7
                          : handleRemove
                }
            />

            <GoalEditModal
                open={Boolean(editGoal)}
                goal={editGoal}
                busy={busy}
                error={actionError}
                onCancel={closeMenus}
                onSave={handleEditSave}
            />
        </section>
    );
};

export default GoalsWorkspace;
